package features.home.ui.components

import kotlinx.html.*
import AppState

object QueryTester {
    fun render(parent: DIV) {
        parent.div("bg-slate-900/50 p-6 rounded-2xl border border-slate-700/50 mb-8") {
            // Header
            h3("font-bold text-slate-300 mb-4 text-sm uppercase tracking-wider") {
                +"🔍 Firestore Query Tester"
            }
            
            // Query Form Container
            div("bg-slate-800/40 p-4 rounded-xl border border-slate-700/50") {
                id = "query-tester-form"
                
                // Collection Path
                div("mb-4") {
                    label("block text-sm font-medium text-slate-400 mb-2") {
                        htmlFor = "query-collection"
                        +"Collection Path:"
                    }
                    input(classes = "w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white") {
                        id = "query-collection"
                        type = InputType.text
                        value = "teams/EzWIASqjmhaJONyUnNtP/attendance"
                        placeholder = "teams/{teamId}/attendance"
                    }
                }
                
                // Where Clause 1
                div("mb-4") {
                    label("block text-sm font-medium text-slate-400 mb-2") {
                        +"WHERE Clause 1:"
                    }
                    div("grid grid-cols-3 gap-2") {
                        input(classes = "bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white") {
                            id = "where1-field"
                            type = InputType.text
                            value = "userId"
                            placeholder = "field"
                        }
                        select("bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white") {
                            id = "where1-op"
                            option { value = "=="; +"==" }
                            option { value = "!="; +"!=" }
                            option { value = ">"; +">" }
                            option { value = ">="; +">=" }
                            option { value = "<"; +"<" }
                            option { value = "<="; +"<=" }
                        }
                        input(classes = "bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white") {
                            id = "where1-value"
                            type = InputType.text
                            value = "cM3Qs0fAZ2XWtUTi2Yt2jHVrIiy2"
                            placeholder = "value"
                        }
                    }
                }
                
                // Where Clause 2
                div("mb-4") {
                    label("block text-sm font-medium text-slate-400 mb-2") {
                        +"WHERE Clause 2:"
                    }
                    div("grid grid-cols-3 gap-2") {
                        input(classes = "bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white") {
                            id = "where2-field"
                            type = InputType.text
                            value = "date"
                            placeholder = "field (leave empty to skip)"
                        }
                        select("bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white") {
                            id = "where2-op"
                            option { value = "=="; +"==" }
                            option { value = "!="; +"!=" }
                            option { value = ">"; +">" }
                            option { value = ">="; +">=" }
                            option { value = "<"; +"<" }
                            option { value = "<="; +"<=" }
                        }
                        input(classes = "bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white") {
                            id = "where2-value"
                            type = InputType.text
                            value = "2026-01-30"
                            placeholder = "value"
                        }
                    }
                }
                
                // Limit
                div("mb-4") {
                    label("block text-sm font-medium text-slate-400 mb-2") {
                        htmlFor = "query-limit"
                        +"Limit:"
                    }
                    input(classes = "w-32 bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white") {
                        id = "query-limit"
                        type = InputType.number
                        value = "1"
                    }
                }
                
                // Test Button
                button(classes = "w-full bg-brand-cyan hover:bg-brand-green text-white font-bold py-3 rounded-xl transition shadow-lg shadow-brand-cyan/20 active:transform active:scale-95") {
                    id = "test-query-btn"
                    attributes["onclick"] = "testFirestoreQuery()"
                    +"🚀 Test Query"
                }
            }
            
            // Results Container
            div("mt-6") {
                id = "query-results"
                // Results will be inserted here
            }
            
            // JavaScript for query testing
            script {
                unsafe {
                    +"""
async function testFirestoreQuery() {
    const resultsDiv = document.getElementById('query-results');
    const btn = document.getElementById('test-query-btn');
    
    // Get form values
    const collection = document.getElementById('query-collection').value;
    const where1Field = document.getElementById('where1-field').value;
    const where1Op = document.getElementById('where1-op').value;
    const where1Value = document.getElementById('where1-value').value;
    const where2Field = document.getElementById('where2-field').value;
    const where2Op = document.getElementById('where2-op').value;
    const where2Value = document.getElementById('where2-value').value;
    const limit = parseInt(document.getElementById('query-limit').value);
    
    // Show loading
    btn.disabled = true;
    btn.textContent = '⏳ Running query...';
    resultsDiv.innerHTML = '<div class="bg-slate-800/40 p-4 rounded-xl border border-slate-700/50 text-slate-400">⏳ Executing query...</div>';
    
    try {
        console.log('🔍 Testing query:', collection);
        
        // Build query
        let query = firebase.firestore().collection(collection);
        
        // Add where clauses
        if (where1Field && where1Value) {
            console.log(`   WHERE ${'$'}{where1Field} ${'$'}{where1Op} ${'$'}{where1Value}`);
            query = query.where(where1Field, where1Op, where1Value);
        }
        
        if (where2Field && where2Value) {
            console.log(`   WHERE ${'$'}{where2Field} ${'$'}{where2Op} ${'$'}{where2Value}`);
            query = query.where(where2Field, where2Op, where2Value);
        }
        
        // Add limit
        if (limit > 0) {
            console.log(`   LIMIT ${'$'}{limit}`);
            query = query.limit(limit);
        }
        
        // Execute query
        const snapshot = await query.get();
        
        console.log(`✅ Query completed. Found ${'$'}{snapshot.size} documents`);
        
        // Display results
        let html = '<div class="bg-slate-800/40 p-4 rounded-xl border border-slate-700/50">';
        html += `<div class="text-brand-cyan font-bold mb-4">✅ Query Results (${'$'}{snapshot.size} documents)</div>`;
        
        if (snapshot.empty) {
            html += '<div class="text-slate-400">📭 No documents found</div>';
        } else {
            snapshot.forEach(doc => {
                const data = doc.data();
                html += '<div class="bg-slate-900/60 p-3 rounded-lg mb-3 border border-slate-700/30">';
                html += `<div class="text-violet-400 font-bold mb-2">📄 Document ID: ${'$'}{doc.id}</div>`;
                html += '<pre class="text-xs text-slate-300 overflow-x-auto">' + JSON.stringify(data, null, 2) + '</pre>';
                html += '</div>';
            });
        }
        
        html += '</div>';
        resultsDiv.innerHTML = html;
        
    } catch (error) {
        console.error('❌ Query failed:', error);
        resultsDiv.innerHTML = `
            <div class="bg-red-500/10 border border-red-500/30 p-4 rounded-xl">
                <div class="text-red-400 font-bold mb-2">❌ Query Failed</div>
                <div class="text-red-300 text-sm">${'$'}{error.message}</div>
                <div class="text-red-400 text-xs mt-2">Code: ${'$'}{error.code || 'unknown'}</div>
            </div>
        `;
    } finally {
        btn.disabled = false;
        btn.textContent = '🚀 Test Query';
    }
}
                    """
                }
            }
        }
    }
}
